import { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import arqamService from '../../services/arqamService';
import Loading from '../../components/Common/Loading';

const formatAmount = (amount, currency, isRtl) => {
  const value = Number(amount) || 0;
  const suffix = currency ? ` ${currency}` : '';
  return `${value.toLocaleString(isRtl ? 'ar-IQ' : 'en-US')}${suffix}`;
};

const statusLabels = {
  pending: ['قيد الانتظار', 'Pending'],
  queued: ['في الطابور', 'Queued'],
  sent: ['مرسلة', 'Sent'],
  delivered: ['مسلّمة', 'Delivered'],
  read: ['مقروءة', 'Read'],
  failed: ['فاشلة', 'Failed'],
};

const ArqamIndex = () => {
  const { isRtl } = useLocalization();
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const t = (ar, en) => (isRtl ? ar : en);

  useEffect(() => {
    loadAccount();
  }, []);

  const loadAccount = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await arqamService.getAccount();
      setAccount(data);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('تعذر جلب بيانات الحساب', 'Failed to load account info');
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loading text={t('جاري التحميل...', 'Loading...')} />;
  }

  return (
    <div className="arqam-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{t('حساب الرسائل (Arqam)', 'SMS Account (Arqam)')}</h1>
            <p className="page-description">
              {t(
                'حالة الحساب ورصيد الرسائل المستخدم لإرسال رموز التحقق OTP',
                'Account status and SMS credit used to send OTP verification codes',
              )}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-alert" style={{ marginBottom: 'var(--sp-5)' }}>
          <span>{error}</span>
        </div>
      )}

      {account && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-card-content">
                <div className={`stat-icon ${account.isActive ? 'stat-icon-success' : 'stat-icon-warning'}`}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <div className="stat-details">
                  <div className="stat-number">{account.status || (account.isActive ? t('نشط', 'Active') : t('موقوف', 'Suspended'))}</div>
                  <div className="stat-label">{t('حالة الحساب', 'Account Status')}</div>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-card-content">
                <div className="stat-icon stat-icon-info">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div className="stat-details">
                  <div className="stat-number">{formatAmount(account.balance, account.currency, isRtl)}</div>
                  <div className="stat-label">{t('الرصيد', 'Balance')}</div>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-card-content">
                <div className="stat-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                  </svg>
                </div>
                <div className="stat-details">
                  <div className="stat-number">{formatAmount(account.bonusBalance, account.currency, isRtl)}</div>
                  <div className="stat-label">{t('رصيد إضافي', 'Bonus Balance')}</div>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-card-content">
                <div className="stat-icon stat-icon-success">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5"></path>
                  </svg>
                </div>
                <div className="stat-details">
                  <div className="stat-number">{formatAmount(account.available, account.currency, isRtl)}</div>
                  <div className="stat-label">{t('المتاح للاستخدام', 'Available')}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">{t('تفاصيل الحساب', 'Account Details')}</h3>
            </div>
            <div className="card-body">
              <table className="table">
                <tbody>
                  <tr>
                    <td>{t('البريد الإلكتروني', 'Email')}</td>
                    <td>{account.email || '—'}</td>
                  </tr>
                  <tr>
                    <td>{t('الاسم', 'Full Name')}</td>
                    <td>{account.fullName || '—'}</td>
                  </tr>
                  <tr>
                    <td>{t('الوضع', 'Mode')}</td>
                    <td>{account.mode || '—'}</td>
                  </tr>
                  <tr>
                    <td>{t('إجمالي المصروف', 'Total Spent')}</td>
                    <td>{formatAmount(account.totalSpent, account.currency, isRtl)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {account.project && (
            <div className="card" style={{ marginTop: 'var(--sp-5)' }}>
              <div className="card-header">
                <h3 className="card-title">{t('المشروع والميزانية', 'Project & Budget')}</h3>
              </div>
              <div className="card-body">
                <table className="table">
                  <tbody>
                    <tr>
                      <td>{t('اسم المشروع', 'Project Name')}</td>
                      <td>{account.project.name || '—'}</td>
                    </tr>
                    <tr>
                      <td>{t('حالة المشروع', 'Project Status')}</td>
                      <td>{account.project.isActive ? t('نشط', 'Active') : t('موقوف', 'Suspended')}</td>
                    </tr>
                    {account.project.budget && (
                      <>
                        <tr>
                          <td>{t('حد الميزانية', 'Budget Limit')}</td>
                          <td>
                            {account.project.budget.isUnlimited
                              ? t('غير محدود', 'Unlimited')
                              : formatAmount(account.project.budget.limit, account.project.budget.currency, isRtl)}
                          </td>
                        </tr>
                        <tr>
                          <td>{t('المصروف', 'Spent')}</td>
                          <td>{formatAmount(account.project.budget.spent, account.project.budget.currency, isRtl)}</td>
                        </tr>
                        <tr>
                          <td>{t('المتبقي', 'Remaining')}</td>
                          <td>
                            {account.project.budget.isUnlimited
                              ? '—'
                              : formatAmount(account.project.budget.remaining, account.project.budget.currency, isRtl)}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {account.messages && (
            <>
              <div className="page-header" style={{ marginTop: 'var(--sp-5)' }}>
                <h3 className="card-title">{t('إحصائيات الرسائل', 'Message Statistics')}</h3>
              </div>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-card-content">
                    <div className="stat-details">
                      <div className="stat-number">{formatAmount(account.messages.total, '', isRtl)}</div>
                      <div className="stat-label">{t('إجمالي الرسائل', 'Total')}</div>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-content">
                    <div className="stat-details">
                      <div className="stat-number">{formatAmount(account.messages.sent, '', isRtl)}</div>
                      <div className="stat-label">{t('المرسلة', 'Sent')}</div>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-content">
                    <div className="stat-details">
                      <div className="stat-number">{formatAmount(account.messages.delivered, '', isRtl)}</div>
                      <div className="stat-label">{t('المسلّمة', 'Delivered')}</div>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-content">
                    <div className="stat-details">
                      <div className="stat-number">{formatAmount(account.messages.failed, '', isRtl)}</div>
                      <div className="stat-label">{t('الفاشلة', 'Failed')}</div>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-content">
                    <div className="stat-details">
                      <div className="stat-number">{formatAmount(account.messages.pending, '', isRtl)}</div>
                      <div className="stat-label">{t('قيد الانتظار', 'Pending')}</div>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-content">
                    <div className="stat-details">
                      <div className="stat-number">{formatAmount(account.messages.deliveryRate, '%', isRtl)}</div>
                      <div className="stat-label">{t('نسبة التسليم', 'Delivery Rate')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {account.messages.byStatus && (
                <div className="card" style={{ marginTop: 'var(--sp-5)' }}>
                  <div className="card-header">
                    <h3 className="card-title">{t('الرسائل حسب الحالة', 'Messages by Status')}</h3>
                  </div>
                  <div className="card-body">
                    <table className="table">
                      <tbody>
                        {Object.entries(account.messages.byStatus).map(([status, count]) => (
                          <tr key={status}>
                            <td>{statusLabels[status] ? t(statusLabels[status][0], statusLabels[status][1]) : status}</td>
                            <td>{formatAmount(count, '', isRtl)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {account.pricing && (
            <div className="card" style={{ marginTop: 'var(--sp-5)' }}>
              <div className="card-header">
                <h3 className="card-title">{t('الأسعار (لكل رسالة)', 'Pricing (per message)')}</h3>
              </div>
              <div className="card-body">
                <table className="table">
                  <tbody>
                    {account.pricing.sms && (
                      <>
                        <tr>
                          <td>{t('SMS - آسياسيل', 'SMS - Asiacell')}</td>
                          <td>{formatAmount(account.pricing.sms.asiacell, account.pricing.currency, isRtl)}</td>
                        </tr>
                        <tr>
                          <td>{t('SMS - كورك', 'SMS - Korek')}</td>
                          <td>{formatAmount(account.pricing.sms.korek, account.pricing.currency, isRtl)}</td>
                        </tr>
                        <tr>
                          <td>{t('SMS - زين', 'SMS - Zain')}</td>
                          <td>{formatAmount(account.pricing.sms.zain, account.pricing.currency, isRtl)}</td>
                        </tr>
                      </>
                    )}
                    {account.pricing.whatsApp && (
                      <>
                        <tr>
                          <td>{t('واتساب - رمز تحقق', 'WhatsApp - OTP')}</td>
                          <td>{formatAmount(account.pricing.whatsApp.otp, account.pricing.currency, isRtl)}</td>
                        </tr>
                        <tr>
                          <td>{t('واتساب - خدمي', 'WhatsApp - Utility')}</td>
                          <td>{formatAmount(account.pricing.whatsApp.utility, account.pricing.currency, isRtl)}</td>
                        </tr>
                        <tr>
                          <td>{t('واتساب - تسويقي', 'WhatsApp - Marketing')}</td>
                          <td>{formatAmount(account.pricing.whatsApp.marketing, account.pricing.currency, isRtl)}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ArqamIndex;
